/*
 * Converts a string to title case
 * @param {string} str - The string to convert
 * @returns {string} The string in title case
 * @example
 * toTitleCase('hello_world') // 'Hello World'
 * toTitleCase('HELLO_WORLD') // 'Hello World'
 * toTitleCase('hello world') // 'Hello World'
 */
export const toTitleCase = (str?: string) => {
  if (!str) return ''

  try {
    return str
      .replace(/_/g, ' ') // Replace underscores with spaces
      .toLowerCase() // Convert to lowercase
      .split(' ') // Split into words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(' ')
  } catch {
    return str
  }
}

/*
 * Converts a string from snake_case to spaces
 * @param {string} str - The string to convert
 * @param {boolean} toLowerCase - Whether to convert the string to lowercase
 * @returns {string} The string with spaces
 * @example
 * snakeCaseToSpaces('HELLO_WORLD') // 'HELLO WORLD'
 * snakeCaseToSpaces('HELLO_WORLD', true) // 'hello world'
 */
export const snakeCaseToSpaces = (str: string, toLowerCase?: boolean) => {
  if (!str) return ''

  try {
    return toLowerCase
      ? str.replace(/_/g, ' ').toLowerCase()
      : str.replace(/_/g, ' ')
  } catch {
    return str
  }
}

/*
 * Split a string at uppercase letters and add a space
 * @param {string} str - The string to split
 * @returns {string} The string with spaces
 * @example
 * splitAtUppercaseAndAddSpace('helloWorld') // 'hello World'
 * splitAtUppercaseAndAddSpace('HelloWorld') // 'Hello World'
 * splitAtUppercaseAndAddSpace('HelloMyWorld') // 'Hello My World'
 */
export const splitAtUppercaseAndAddSpace = (str: string) =>
  str.split(/(?=[A-Z])/).join(' ')

const SHORT_CONJUNCTIONS = [
  'and',
  'as',
  'but',
  'for',
  'if',
  'nor',
  'or',
  'so',
  'yet',
]
const ARTICLES = ['a', 'an', 'the']
const SHORT_PREPOSITIONS = [
  'as',
  'at',
  'by',
  'for',
  'in',
  'of',
  'off',
  'on',
  'per',
  'up',
  'via',
]
const KNOWN_ACRONYMS = ['llc', 'dba']
const LOWERCASE_ONLY_WORDS = [
  ...SHORT_CONJUNCTIONS,
  ...ARTICLES,
  ...SHORT_PREPOSITIONS,
]

/**
 * Format a string in Title Case according to:
 * https://apastyle.apa.org/style-grammar-guidelines/capitalization/title-case
 */
export const apaTitleCase = (input: string) => {
  const words = input.toLocaleLowerCase().split(/\b/)

  const determineCasing = (
    word: string,
    index: number,
    allWords: string[]
  ): 'UPPER' | 'LOWER' | 'TITLE' => {
    if (KNOWN_ACRONYMS.includes(word)) {
      return 'UPPER'
    }
    // Start of a sentence
    if (index === 0 || allWords[index - 1] === '.') {
      return 'TITLE'
    }
    if (allWords[index - 1] === "'") {
      return 'LOWER'
    }
    return LOWERCASE_ONLY_WORDS.includes(word) ? 'LOWER' : 'TITLE'
  }
  const formatWord = (word: string, index: number, allWords: string[]) => {
    switch (determineCasing(word, index, allWords)) {
      case 'UPPER':
        return word.toLocaleUpperCase()
      case 'LOWER':
        return word.toLocaleLowerCase()
      case 'TITLE':
        return word.charAt(0).toUpperCase() + word.slice(1)
      default:
        throw new Error('should never happen')
    }
  }

  return words.map(formatWord).join('')
}
